/** NPC speed source window; shared speed folds, plus the native bounded token pass. */
import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { applyPilotedSpeeds } from './piloted-object';
import { readNodeItemId, readLayerScoringToken } from './layer-cell-readers';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { createStatusLayerReadContext } from './status-row-construction';
import { createFormulaReadContext } from './formula-read-context';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import { scoreCarrierIntegerLayer, scoreNamedIntegerFragment } from './formula-variable-resolution';
import { integerFragmentBraces } from './status-integer-contribution';
import { capInventorySourceRows } from './computed-persistence';
import { foldCreatureWalkingSpeedSources, foldCreaturePlainSpeedSources } from './creature-walking-speed';
import { foldCreatureDerivedSpeedSources } from './creature-derived-speed';
import { applyCreatureSpeedFactorRows, foldShieldedSpeed, statusDefinitionShieldsSpeed } from './creature-speed-factor';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { compileCreatureDerivedJump } from './creature-derived-jump';
import { compileCreatureJumpOverride } from './creature-jump-override';
import { foldNativeCreatureJump } from './passive-integer-fold';
import { suppressCreatureSpeedEchoes } from './creature-speed-echo-suppression';
import type { CreatureSpeedSource } from '../types';
import { isPhpNumeric } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';
import type { CreatureSpeedsDependencies } from './creature-speeds';
import { pristineFragments } from './creature-attacks';
export type { CreatureSpeedsDependencies } from './creature-speeds';

type Row = Record<string, unknown>;
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Row)[key] : undefined;
const clone = <T>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value)) as T;
const scalar = (value: unknown): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, '0') ? own(value, '0') : value;
const stringValue = (value: unknown): string => typeof scalar(value) === 'string' ? String(scalar(value)) : '';
const numberValue = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
const PLAIN_LAYERS = ['speed_flying', 'speed_burrowing', 'speed_hovering', 'teleport'] as const;
const ECHO_NAMES = ['swimming', 'climbing', 'flying', 'burrowing', 'hovering'] as const;
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export type CreatureSpeedsCompileResult = { ok: true; speeds: Row } | { ok: false; reason: string };

function selfFactor(value: unknown, layer: string): number | null {
    const token = readLayerScoringToken(value);
    if (token === null) return null;
    const escaped = layer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const left = new RegExp(`^(\\d*\\.?\\d+)\\s*\\*\\s*\\[${escaped}\\]$`).exec(token.trim());
    const right = new RegExp(`^\\[${escaped}\\]\\s*\\*\\s*(\\d*\\.?\\d+)$`).exec(token.trim());
    return left || right ? Number((left ?? right)![1]) : null;
}
function sourceRow(category: string, label: string, value: number, hrefId: string, kind: string, brace: number | null = null): CreatureSpeedSource {
    return { category, label: label.trim() === '' ? 'Unknown source' : label, value, href_id: hrefId, kind,
        ...(brace === null ? {} : { value_brace: brace }) };
}
/** StatusEffect::getId('riding' / 'piloted' / 'piloting') — compute_speeds §6's mount / vehicle statuses. */
const STATUS_RIDING_ID = 'FF8D219A-2A83-0EE5-A222-E748753E8D0B', STATUS_PILOTED_ID = 'DDC66C31-4093-84AF-18BB-093D924B8738', STATUS_PILOTING_ID = 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12';
function statusDefinitionId(status: ItemFieldRow, def: ItemFieldRow): string {
    const direct = stringValue(own(status, 'status_item_id'));
    const fromItems = stringValue(own(own(def, 'items'), 'item_id'));
    return validUuid(direct) ? direct : validUuid(fromItems) ? fromItems : '';
}
function foldBrace(fragments: unknown[]): number | null {
    const braces = integerFragmentBraces(fragments);
    return braces.length ? Number(braces.reduce((left, right) => left > right ? left : right)) : null;
}
function speedShort(layer: string): string { return layer === 'teleport' ? 'teleport' : layer.slice(6); }

/** Native speed composition over the constructed NPC and its source graph. */
/** `options.shield` overrides the speed_cannot_be_reduced detection (false = the PHP-equal UNSHIELDED compile — the C-PI speed-shield
 *  fold's parity check; true / absent = detect from the held statuses). */
export function compileNpcSpeeds(input: ItemFieldRow, deps: CreatureSpeedsDependencies, options: { shield?: boolean; form?: ItemFieldRow | null } = {}): CreatureSpeedsCompileResult {
    const priorComputed = arrayLike(own(input, 'computed')) ? own(input, 'computed') as Row : {};
    const priorCreature = arrayLike(own(priorComputed, 'creature')) ? own(priorComputed, 'creature') as Row : {};
    const host = { ...input, computed: { ...priorComputed, creature: { ...priorCreature } } };
    const form = options.form ?? null, baseHost = form ?? host;
    const readers = createStatusLayerReadContext(deps);
    const formulaReaders = createFormulaReadContext(deps);
    const providers = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId: readNodeItemId,
        resolveCarrierRef: deps.resolveCarrierRef });
    const dynamicCollector = createDynamicIntegerSourceCollector(deps);
    const cleaner = createComputeSourceCleaner(deps);
    const payloadStatuses = Object.values(readCreaturePayloadStatuses(host, readers.layerValue, deps.resolveCarrierRef)).filter(arrayLike);
    // ft_compute_collect_dynamic_speed_sources' passive entry_gate (2026-10-07): an AURA passive's speed is its zone occupants' (an applied
    // status), and a combat-start TIMED passive's (Thief L40 Grand Planar Thief / Clean Getaway, Machinist Gear Sprint) is the window
    // status row its trigger writes — both are scored by the status walk only. Without it the bucket copy gave +20 always, +40 in the window.
    const held = (row: ItemFieldRow, name: string): boolean => { const v = readers.layerValue(row, name); return v !== null && v !== undefined; };
    const rootPassives = new Set(Object.values(readers.layerEntries({ passive_ability: own(host, 'passive_ability') ?? [] }, 'passive_ability'))
        .map(readLayerListEntryUuid).filter(Boolean));
    const speedPassiveGate = (entry: unknown, def: ItemFieldRow): boolean =>
        rootPassives.has(readLayerListEntryUuid(entry))
        && !['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(name => held(def, name))
        && !(held(def, 'triggers_after_combat_begins') && held(def, 'duration_type'));
    // speed_cannot_be_reduced (Wizard v9 "Ever-Changing Form", 2026-10-09): only a HELD status row carries it (its timed passive's bucket
    // copy never counts — the gate above); while held, every reducing dynamic row / slow factor / lower lock is ignored (foldShieldedSpeed)
    const attachedPassives = (def: unknown): unknown[] => Object.values(readers.layerEntries(def as ItemFieldRow, 'passive_ability'))
        .map(entry => { const id = readLayerListEntryUuid(entry); return validUuid(id) ? deps.resolveCarrierRef(id) : arrayLike(entry) ? entry : null; });
    const speedShield = options.shield !== false && payloadStatuses.some(status => statusDefinitionShieldsSpeed(own(status, 'status_arr'), readers.layerValue as never, attachedPassives));

    // Paired vehicle statuses require their own raw closure.
    let mountRestricted = false;
    for (const status of payloadStatuses) {
        const def = own(status, 'status_arr');
        // Codex review 2026-09-23: a held status without a resolved definition would skip every refusal below and its speed rows.
        if (!arrayLike(def)) return { ok: false, reason: 'speed-status-undefined' };
        const name = arrayLike(def) ? stringValue(own(own(def, 'items'), 'item_name')).toLowerCase() : '';
        if (formulaReaders.itemField(status, 'item_type') === 'creature' && !form) return { ok: false, reason: 'speed-shapeshift' };
        const sid = statusDefinitionId(status, def).toUpperCase();
        if ((sid === STATUS_PILOTING_ID || name === 'piloting') && !deps.pilotedObject) return { ok: false, reason: 'speed-mount-vehicle' };
        if (sid === STATUS_RIDING_ID || sid === STATUS_PILOTED_ID) mountRestricted = true;
        // (a status's NESTED ability buckets — 2026-09-25, R-042: scored per status right after its own row, as PHP's walk does; the
        // refusal that stood here is lifted)
    }
    const hostLayers = arrayLike(own(host, 'item_layer_types')) ? own(host, 'item_layer_types') as Row : {};
    if (['piloting', 'piloted_object'].some(layer => Object.prototype.hasOwnProperty.call(hostLayers, layer))) {
        return { ok: false, reason: 'speed-mount-vehicle' };
    }

    // A stored possessor carries the previous compute-owned cluster. PHP rebuilds from a fresh arr; remove it before any
    // formula read so Hasted's cross-speed fragments do not accidentally consume stale totals.
    const hostRow = host as Row;
    const computed = arrayLike(own(host, 'computed')) ? own(host, 'computed') as Row : (hostRow.computed = {} as Row);
    const creature = arrayLike(own(computed, 'creature')) ? own(computed, 'creature') as Row : (computed.creature = {} as Row);
    delete creature.speeds;

    const baseSources = (layer: string): CreatureSpeedSource[] => {
        const out: CreatureSpeedSource[] = [];
        const direct = pristineFragments(baseHost, layer);
        if (direct.length) {
            const only = { item_layer_types: { [layer]: direct } } as ItemFieldRow;
            const value = scoreCarrierIntegerLayer(only, layer, 0, false, deps.metadata, formulaReaders);
            if (value !== 0) out.push(sourceRow('host', stringValue(formulaReaders.itemField(baseHost, 'item_name')) || 'Host', value,
                stringValue(formulaReaders.itemField(baseHost, 'item_id')), 'host'));
        }
        for (const carrier of providers.providers(baseHost)) {
            const fragments = pristineFragments(carrier, layer);
            if (!fragments.length) continue;
            const value = scoreCarrierIntegerLayer({ item_layer_types: { [layer]: fragments } }, layer, 0, false, deps.metadata, formulaReaders);
            if (value === 0) continue;
            const items = own(carrier, 'items');
            const type = stringValue(own(items, 'item_type')) || 'carrier';
            out.push(sourceRow(type, stringValue(own(items, 'item_name')), value, stringValue(own(items, 'item_id')), type));
        }
        return out;
    };
    const explicitPresent = (layer: string): boolean => {
        if (pristineFragments(baseHost, layer).length) return true;
        for (const carrier of providers.providers(baseHost)) if (pristineFragments(carrier, layer).length) return true;
        return false;
    };
    const dynamic = (layer: string): { sources: CreatureSpeedSource[]; brace: number | null; statusIds: Set<string> } => {
        // the generic walk WITHOUT the statuses' nested rows — they are interleaved per status below (PHP: own row, then nested)
        const [generic] = dynamicCollector.dynamic(host, layer, true, true, form, false, speedPassiveGate);
        // ft_compute_collect_dynamic_speed_sources caps the equipped rows (ft_cap_inventory_equipped_source_rows: one highest item)
        const [capped] = capInventorySourceRows(generic);
        const sources = (Object.values(capped) as unknown as CreatureSpeedSource[]).filter(row => row.kind !== 'status_effect').map(row => ({ ...row }));
        // PHP orders the rows passive, status, equipped (capped), feat (ft_compute_collect_dynamic_speed_sources); the generic walk already
        // yields passive, equipped, feat, so this walk's status rows go in after the passive block. History: 2026-09-23 refused every
        // passive / feat row and gear beside a speed status (order + the pre-status snapshot, then reconstructed from these rows); 2026-09-25
        // (real-PC census: 13 of 35 PCs) the snapshot is scored through layer_entries like PHP (below) and the order is restored here — only
        // a BRACED / factor generic row stays unproven.
        const plain = (row: CreatureSpeedSource): boolean => (row as Row).value_brace === undefined && (row as Row).value_factor === undefined;
        if (!sources.every(row => ['passive_ability', 'inventory_equipped', 'feat'].includes(String(row.kind)) && plain(row))) throw new Error('speed-generic-dynamic');
        const passiveRows = sources.filter(row => row.kind === 'passive_ability'), laterRows = sources.filter(row => row.kind !== 'passive_ability');
        if (sources.some((row, i) => i < passiveRows.length ? row.kind !== 'passive_ability' : row.kind === 'passive_ability')) throw new Error('speed-generic-order');
        sources.length = 0; sources.push(...passiveRows);
        const statusIds = new Set<string>();
        let brace: number | null = null;
        let formPending = form !== null;
        for (const status of payloadStatuses) {
            const selected = formPending && formulaReaders.itemField(status, 'item_type') === 'creature';
            if (selected) formPending = false;
            const def = selected ? form : own(status, 'status_arr');
            if (!arrayLike(def)) continue;
            const fragments = selected ? [] : readers.layerEntries(def, layer);
            const nested = dynamicCollector.nestedStatusRows(host, def, layer) as unknown as CreatureSpeedSource[];
            if (!Object.keys(fragments).length) {
                for (const row of nested) { const b = (row as Row).value_brace; if (typeof b === 'number') brace = brace === null ? b : Math.max(brace, b); sources.push({ ...row }); }
                continue;
            }
            const id = statusDefinitionId(status, def); if (id) statusIds.add(id.toUpperCase());
            const label = stringValue(own(own(def, 'items'), 'item_name')) || 'Status effect';
            const ordinary: Row = {};
            for (const [key, fragment] of Object.entries(fragments)) {
                const factor = selfFactor(fragment, layer);
                if (factor !== null) {
                    sources.push({ ...sourceRow('status_effect', label, 0, id, 'status_effect'), value_factor: factor });
                    continue;
                }
                // A cross-speed reference ("1.5*[speed_walking]" on climbing — Rushed) is NOT a factor row: PHP scores it with
                // score_named_integer_layer_fragment against the FRESH compile arr, whose speeds are not yet resolved — the stored
                // cluster is deleted above for exactly that reason, so the same scorer resolves the same token (2026-09-24: the Rushed
                // corpus — climb / swim stay the ½-walk 15, fly / burrow / hover stay absent). Only that ZERO case is proven: a host
                const token = readLayerScoringToken(fragment) ?? '';
                if (token.includes('[') && scoreNamedIntegerFragment(host, { 0: fragment }, layer, deps.metadata, formulaReaders) !== 0) throw new Error('speed-token-cross-nonzero');
                ordinary[key] = fragment;
            }
            if (Object.keys(ordinary).length) {
                const localBrace = foldBrace(Object.values(ordinary));
                const value = scoreNamedIntegerFragment(host, ordinary, layer, deps.metadata, formulaReaders);
                if (localBrace !== null) brace = brace === null ? localBrace : Math.max(brace, localBrace);
                if (value !== 0 || localBrace !== null) sources.push(sourceRow('status_effect', label, value, id, 'status_effect', localBrace));
            }
            for (const row of nested) { const b = (row as Row).value_brace; if (typeof b === 'number') brace = brace === null ? b : Math.max(brace, b); sources.push({ ...row }); }
        }
        sources.push(...laterRows);
        return { sources, brace, statusIds };
    };
    const cleanFold = (fold: Row): Row => {
        const out = clone(fold);
        if (Array.isArray(out.sources)) {
            const clean = cleaner.clean(out.sources as ItemFieldRow, host);
            if (Object.keys(clean).length) out.sources = clean; else delete out.sources;
        }
        if (arrayLike(out.breakdown) && Object.keys(out.breakdown).length === 0) delete out.breakdown;
        return out;
    };

    try {
        const walkBase = baseSources('speed_walking');
        const walkDyn = dynamic('speed_walking');
        const walkFold = foldShieldedSpeed(speedShield, walkDyn.sources, walkDyn.brace,
            (sources, brace) => foldCreatureWalkingSpeedSources({ sources: [...walkBase, ...sources], dynamicBrace: brace }));
        const walkSources = (walkFold.sources ?? []).map(source => ({ ...source }));
        const walkPrefactor = applyCreatureSpeedFactorRows(walkSources, walkSources.reduce((sum, source) => sum + source.value, 0), walkFold.locked).prefactor;
        // Only the live status window creates a pre-status snapshot. A static
        // template derives from its resolved walk, without inventing one.
        const basis = readItemField(host, 'derivative_speed_basis');
        const rawBasis = readItemField(arrayLike(basis) ? basis : null, 'speed_walking');
        const snapshot = isPhpNumeric(rawBasis) ? Number(rawBasis) : 0;
        const walkBasis = snapshot > 0 ? Math.min(snapshot, walkFold.total) : walkFold.total;
        const walkBasisPrefactor = snapshot > 0 ? Math.min(snapshot, walkPrefactor) : walkPrefactor;
        const speeds: Row = { walking: cleanFold(walkFold as unknown as Row) };
        const totals: Row = { walking: walkFold.total };

        for (const layer of ['speed_swimming', 'speed_climbing'] as const) {
            const base = baseSources(layer), dyn = dynamic(layer);
            const fold = foldShieldedSpeed(speedShield, dyn.sources, dyn.brace, (sources, brace) => foldCreatureDerivedSpeedSources({
                defaultDerivation: !explicitPresent(layer), walkBasis, walkBasisPrefactor, baseSources: base, dynamicSources: sources, dynamicBrace: brace }));
            speeds[speedShort(layer)] = cleanFold(fold as unknown as Row); totals[speedShort(layer)] = fold.total;
        }
        for (const layer of PLAIN_LAYERS) {
            const dyn = dynamic(layer);
            const plainBase = baseSources(layer);
            const fold = foldShieldedSpeed(speedShield, dyn.sources, dyn.brace,
                (sources, brace) => foldCreaturePlainSpeedSources({ sources: [...plainBase, ...sources], dynamicBrace: brace }));
            const short = speedShort(layer); totals[short] = fold.total;
            // ft_compute_persist zero-elides only a genuinely empty unlocked zero; factor/brace provenance keeps a wrapper.
            if (fold.total !== 0 || fold.sources?.length || fold.locked) speeds[short] = cleanFold(fold as unknown as Row);
        }

        // Native §3.5: resolve authored token chains against the complete speed
        // cluster, in fixed order, for at most three passes.
        creature.speeds = speeds;
        for (let pass = 0; pass < 3; pass++) {
            let changed = false;
            for (const layer of ['speed_swimming', 'speed_climbing', 'speed_flying', 'speed_burrowing', 'speed_hovering']) {
                const fragments: unknown[] = [];
                let label = '', href = '';
                for (const row of [baseHost, ...providers.providers(baseHost)]) for (const entry of pristineFragments(row, layer)) {
                    const token = readLayerScoringToken(entry);
                    if (token === null || !token.includes('[')) continue;
                    fragments.push(token);
                    if (!label) { label = stringValue(formulaReaders.itemField(row, 'item_name')) || (row === baseHost ? 'Host' : 'Carrier'); href = stringValue(formulaReaders.itemField(row, 'item_id')); }
                }
                if (!fragments.length) continue;
                const tokenBase = scoreCarrierIntegerLayer({ ...host, item_layer_types: { [layer]: fragments } }, layer, 0, false, deps.metadata, formulaReaders);
                if (tokenBase <= 0) continue;
                const dyn = dynamic(layer);
                if (dyn.brace !== null) continue;
                const value = applyCreatureSpeedFactorRows(dyn.sources, tokenBase + dyn.sources.reduce((sum, row) => sum + row.value, 0), false).total;
                const short = speedShort(layer);
                if (value <= numberValue(totals[short])) continue;
                const rows = [sourceRow('derived', label || short[0]!.toUpperCase() + short.slice(1) + ' (derived)', tokenBase, href, 'derived'), ...dyn.sources];
                speeds[short] = cleanFold({ total: value, base: 0, locked: false, sources: rows,
                    breakdown: Object.fromEntries(rows.reduce((map, row) => { const key = row.category ?? row.kind; map.set(key, (map.get(key) ?? 0) + row.value); return map; }, new Map<string, number>())) });
                totals[short] = value; changed = true;
            }
            if (!changed) break;
        }

        const jumpBase = baseSources('speed_jumping');
        const authoredJumpBase = jumpBase.reduce((sum, source) => sum + source.value, 0);
        // The native jump fold owns every dynamic source and multiplier. Seed it
        // only from pristine host/form providers, never a prior multiplied total.
        const override = compileCreatureJumpOverride({ walk: walkFold.total, authoredJumpBase,
            explicitJump: authoredJumpBase > 0 ? authoredJumpBase : null,
            baseSources: jumpBase, dynamicSources: [], dynamicBrace: null });
        const body = numberValue(readers.layerValue(host, 'body'));
        const selectedJump = override.handled ? override.persisted
            : compileCreatureDerivedJump({ walk: walkFold.total, body });
        const jump = selectedJump ? foldNativeCreatureJump(host, deps, cleanFold(selectedJump as unknown as Row), walkFold.total, speedShield) : null;
        if (jump) { speeds.jumping = jump; totals.jumping = numberValue(jump.total); } else totals.jumping = 0;

        const storedPresence = readItemField(host, 'pre_status_speed_layer_presence');
        const presence = arrayLike(storedPresence) ? Object.fromEntries(ECHO_NAMES.map(name =>
            [`speed_${name}`, phpJsonTruthy(readItemField(storedPresence, `speed_${name}`))])) : null;
        const echo = suppressCreatureSpeedEchoes({ walkTotal: walkFold.total, jumpTotal: numberValue(totals.jumping), totals: totals as Record<typeof ECHO_NAMES[number], number>, preStatusPresence: presence });
        for (const short of echo.suppressed) { delete speeds[short]; totals[short] = 0; }
        // compute_speeds §6 (Riding / Piloted): every non-teleport speed is zeroed and locked, its resolved value kept as pre_mount;
        // ft_compute_persist replaces the slot wholesale (an existing key keeps its position, a new one appends)
        if (mountRestricted) {
            const resolved: Row = { walking: walkFold.total, swimming: totals.swimming, climbing: totals.climbing, jumping: totals.jumping,
                flying: totals.flying, burrowing: totals.burrowing, hovering: totals.hovering };
            for (const short of ['walking', 'swimming', 'climbing', 'jumping', 'flying', 'burrowing', 'hovering']) {
                const pre = numberValue(resolved[short] ?? 0);
                speeds[short] = { total: 0, base: 0, locked: true, ...(pre !== 0 ? { pre_mount: pre } : {}) };
            }
        }
        if (!mountRestricted && deps.pilotedObject) {
            const vehicle = deps.pilotedObject(input);
            if (vehicle) applyPilotedSpeeds(speeds, vehicle, (row, key) => scoreCarrierIntegerLayer(row, key, 0, false, deps.metadata, formulaReaders));
        }
        return { ok: true, speeds };
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (message.startsWith('speed-') || message.startsWith('unsupported-')) return { ok: false, reason: message };
        return { ok: false, reason: `speed-compile-error:${message.slice(0, 80)}` };
    }
}
